<template>
    <div ref="sentinel" class="infinite-loading-container">
        <slot v-if="state === 'loading'" name="spinner">
            <div class="has-text-centered p-4">
                <b-loading :is-full-page="false" :active="true" />
            </div>
        </slot>
        <slot v-if="state === 'complete'" name="complete" />
        <slot v-if="state === 'error'" name="error">
            <div class="has-text-centered p-4 has-text-danger">
                {{ $t('Error loading data.') }}
            </div>
        </slot>
    </div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount } from 'vue';
import {
    resolveScrollRoot,
    visibleBand,
    isSentinelReached,
    decideLoad,
    prefetchDistance
} from '../utils/infiniteScroll';

const props = defineProps({
    identifier: { type: [Number, String], default: 0 },
    /**
     * How far ahead the next page is asked for, as a number of screens of the
     * list. The point is that the reader never meets the end of what is loaded:
     * several screens of rows are already there when they get to the bottom of
     * the current one, so a long list scrolls as if it had no pages.
     */
    prefetchScreens: { type: Number, default: 6 },
    /** Floor for that distance, for a list rendered in a very short box. */
    distance: { type: Number, default: 100 },
    /**
     * How many pages may be pulled in a row without the reader scrolling. The
     * prefetch buffer is what normally ends a burst — this is the backstop that
     * keeps a future layout change from turning into a section loading itself
     * whole.
     */
    maxAutoLoads: { type: Number, default: 8 }
});

const emit = defineEmits(['infinite']);

const sentinel = ref(null);
const state = ref('ready'); // 'ready' | 'loading' | 'complete' | 'error'
let observer = null;
let observedRoot = null;
let active = false;
// Pages pulled since the reader last scrolled: the budget the geometry check is
// backed up by.
let autoLoads = 0;
let scrollTick = null;
// Distance used for the last observer, so the observer is only rebuilt when the
// box it watches really changed shape.
let observedMargin = null;

const stateHandler = {
    loaded() {
        if (!active) return;
        state.value = 'ready';
        // Re-armed on the next frame, not after a delay: the page that just
        // landed may not have been enough to refill the buffer, and a pause
        // here is a pause the reader feels. What stops the chain is the buffer
        // being full, which the geometry check reads directly.
        nextCheck();
    },
    complete() {
        if (!active) return;
        state.value = 'complete';
    },
    error() {
        if (!active) return;
        state.value = 'error';
    }
};

function nextCheck() {
    if (!observer || !sentinel.value) return;

    observer.unobserve(sentinel.value);
    requestAnimationFrame(() => {
        if (state.value === 'ready') {
            // The layout the sentinel lives in is not the one it was born in:
            // an empty list scrolls nowhere, and a filled one scrolls inside a
            // box that only became the scroller once the rows arrived. So the
            // root is resolved again on every re-arm.
            attach();
        }
    });
}

/**
 * The boxes between the sentinel and the document, as plain rectangles — what
 * the decision functions in utils/infiniteScroll.js read.
 */
function ancestorBoxes() {
    const boxes = [];

    for (let el = sentinel.value?.parentElement; el; el = el.parentElement) {
        const rect = el.getBoundingClientRect();
        boxes.push({
            el,
            overflowY: getComputedStyle(el).overflowY,
            scrollHeight: el.scrollHeight,
            clientHeight: el.clientHeight,
            top: rect.top,
            bottom: rect.bottom
        });
    }

    return boxes;
}

/**
 * How far below the fold the list keeps rows loaded, for the box it is
 * currently rendered in — a full-width table, a master-detail panel and a
 * kanban column are not the same height, so the buffer cannot be a constant.
 */
function currentDistance(boxes) {
    const { top, bottom } = visibleBand(boxes, window.innerHeight);

    return prefetchDistance({
        bandHeight: Math.max(0, bottom - top),
        screens: props.prefetchScreens,
        minimum: props.distance
    });
}

/**
 * The decision itself, taken from whatever woke us up — the observer or a
 * scroll. The observer is only a trigger: rooted on a box that turns out not to
 * clip, it reports "visible" once and then never changes again, so a scroll must
 * be able to ask the same question.
 */
function evaluate() {
    const boxes = ancestorBoxes();
    const rect = sentinel.value?.getBoundingClientRect();

    // `isIntersecting` is only ever a hint here: it is read against one root,
    // and a root that turns out not to clip reports every position as visible.
    // The geometry decides.
    const reached = isSentinelReached({
        sentinel: rect && { top: rect.top, bottom: rect.bottom },
        ancestors: boxes,
        viewportHeight: window.innerHeight,
        distance: currentDistance(boxes)
    });

    const decision = decideLoad({
        state: state.value,
        reached,
        autoLoads,
        maxAutoLoads: props.maxAutoLoads,
        canScroll: resolveScrollRoot(boxes) !== null
    });

    // 'blocked' — the budget ran out while the sentinel is still on screen. The
    // next scroll resets it; until then the list holds what it has.
    if (decision !== 'load') return;

    autoLoads++;
    state.value = 'loading';
    emit('infinite', stateHandler);
}

function handleIntersect(entries) {
    if (!entries[0]?.isIntersecting) return;
    evaluate();
}

function onAnyScroll() {
    // A scroll is the reader asking for more, so the budget starts over.
    autoLoads = 0;

    if (state.value !== 'ready' || scrollTick) return;

    scrollTick = requestAnimationFrame(() => {
        scrollTick = null;
        evaluate();
    });
}

function attach() {
    if (!sentinel.value) return;

    const boxes = ancestorBoxes();
    const root = resolveScrollRoot(boxes)?.el || null;
    const margin = currentDistance(boxes);

    if (!observer || root !== observedRoot || margin !== observedMargin) {
        observer?.disconnect();
        observedRoot = root;
        observedMargin = margin;
        // The observer wakes us up screens before the end, so the request
        // leaves while the reader still has rows in front of them.
        observer = new IntersectionObserver(handleIntersect, {
            root,
            rootMargin: `0px 0px ${margin}px 0px`
        });
    }

    observer.observe(sentinel.value);
}

function setup() {
    observer?.disconnect();
    observer = null;
    observedRoot = null;
    observedMargin = null;
    state.value = 'ready';
    autoLoads = 0;
    attach();
}

watch(
    () => props.identifier,
    () => {
        setup();
    }
);

onMounted(() => {
    active = true;
    setup();
    // Scroll does not bubble, so it is caught in the capture phase: the list may
    // scroll in the page, in a panel or in a column of its own.
    document.addEventListener('scroll', onAnyScroll, { capture: true, passive: true });
});

onBeforeUnmount(() => {
    active = false;
    document.removeEventListener('scroll', onAnyScroll, { capture: true });
    if (scrollTick) {
        cancelAnimationFrame(scrollTick);
        scrollTick = null;
    }
    if (observer) {
        observer.disconnect();
        observer = null;
    }
});
</script>

<style scoped>
/*
 * A pixel of height, so the sentinel has something to intersect with. A
 * zero-height box resting on the scroll container's edge produces an empty
 * intersection rectangle, which reads as "not visible" — the list then stops
 * loading exactly where it should carry on.
 */
.infinite-loading-container {
    min-height: 1px;
}
</style>
